/**
 * useScrollReveal — IntersectionObserver-based toggle for entrance animations.
 *
 * Watches the canvas tree for elements with `data-wb-anim="<kind>"` and flips
 * their `data-anim-shown="0" → "1"` when they enter the viewport. CSS
 * (`./scrollReveal.css`) handles the actual keyframes / transforms.
 *
 * Re-observes when the blocks list changes (new sections, drag-reorder).
 */
import { useEffect } from 'react';

export function useScrollReveal({ canvasRef, blocks, enabled = true }) {
  useEffect(() => {
    if (!enabled) return;
    const root = canvasRef?.current;
    if (!root) return;

    const els = Array.from(root.querySelectorAll('[data-wb-anim]'));
    if (els.length === 0) return;

    // In the editor we want sections to *always* be visible (no hidden state)
    // so users can edit them. The CSS guards by checking if the user is on
    // the live preview / rendered site. We still wire the observer so a
    // future "preview mode" toggle gets it for free.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-anim-shown', '1');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -80px 0px' },
    );

    for (const el of els) {
      const kind = el.getAttribute('data-wb-anim');
      if (!kind || kind === 'none') {
        el.setAttribute('data-anim-shown', '1');
        continue;
      }
      // If element already in view at mount (e.g. hero), reveal immediately
      // to skip a flash of hidden content.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.setAttribute('data-anim-shown', '1');
      } else {
        observer.observe(el);
      }
    }

    return () => observer.disconnect();
  }, [canvasRef, blocks, enabled]);
}
